<template>
  <div class="ios-calendar"
       :cy="cyRoot"
       :class="{ 'ios-calendar--disabled': disabled }">
    <header class="ios-calendar-header">
      <button type="button"
              class="ios-calendar-nav"
              :cy="`${cyRoot}-prev`"
              :disabled="disabled"
              aria-label="Luna anterioară"
              @click="prevMonth">
        <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
          <path d="M15 6 L9 12 L15 18"
                stroke="currentColor"
                stroke-width="2.5"
                stroke-linecap="round"
                stroke-linejoin="round"
                fill="none" />
        </svg>
      </button>
      <span class="ios-calendar-title"
            :cy="`${cyRoot}-title`">{{ title }}</span>
      <button type="button"
              class="ios-calendar-nav"
              :cy="`${cyRoot}-next`"
              :disabled="disabled"
              aria-label="Luna următoare"
              @click="nextMonth">
        <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
          <path d="M9 6 L15 12 L9 18"
                stroke="currentColor"
                stroke-width="2.5"
                stroke-linecap="round"
                stroke-linejoin="round"
                fill="none" />
        </svg>
      </button>
    </header>

    <div class="ios-calendar-weekdays">
      <span v-for="(w, i) in weekdays" :key="i">{{ w }}</span>
    </div>

    <div class="ios-calendar-grid">
      <button v-for="cell in cells"
              :key="cell.key"
              type="button"
              class="ios-calendar-day"
              :cy="cell.cy"
              :class="{
                'ios-calendar-day--muted': !cell.inMonth,
                'ios-calendar-day--today': cell.isToday && !cell.isSelected,
                'ios-calendar-day--selected': cell.isSelected,
                'ios-calendar-day--marked': cell.hasAppointment && !cell.isSelected,
                'ios-calendar-day--past': cell.isPastDisabled
              }"
              :disabled="disabled || cell.isPastDisabled"
              @click="selectCell(cell)">
        <span>{{ cell.d }}</span>
      </button>
    </div>
  </div>
</template>

<script>
const ISO_RE = /^(\d{4})-(\d{2})-(\d{2})$/
const MONTHS_RO = [
  'Ianuarie', 'Februarie', 'Martie', 'Aprilie', 'Mai', 'Iunie',
  'Iulie', 'August', 'Septembrie', 'Octombrie', 'Noiembrie', 'Decembrie'
]
const WEEKDAYS_RO = ['L', 'M', 'M', 'J', 'V', 'S', 'D']

function pad (n) {
  return String(n).padStart(2, '0')
}

function isoOf (y, m, d) {
  return `${y}-${pad(m + 1)}-${pad(d)}`
}

export default {
  name: 'IOSCalendar',
  props: {
    value: {
      type: String,
      default: ''
    },
    cy: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    // Blocks picking any day before today — the currently selected day stays
    // pickable even if it's in the past, so editing an appointment that's
    // already on a past date doesn't lock the field.
    disablePast: {
      type: Boolean,
      default: false
    },
    markedDates: {
      type: Array,
      default: () => []
    }
  },
  data () {
    const today = new Date()
    const parsed = this._parseValue(this.value)
    const anchor = parsed || today
    return {
      viewYear: anchor.getFullYear(),
      viewMonth: anchor.getMonth()
    }
  },
  computed: {
    cyRoot () {
      return this.cy || 'ios-calendar'
    },
    weekdays () {
      return WEEKDAYS_RO
    },
    title () {
      return `${MONTHS_RO[this.viewMonth]} ${this.viewYear}`
    },
    selectedIso () {
      const parsed = this._parseValue(this.value)
      return parsed ? isoOf(parsed.getFullYear(), parsed.getMonth(), parsed.getDate()) : ''
    },
    todayIso () {
      const t = new Date()
      return isoOf(t.getFullYear(), t.getMonth(), t.getDate())
    },
    cells () {
      const first = new Date(this.viewYear, this.viewMonth, 1)
      const firstDow = first.getDay()
      const offset = (firstDow + 6) % 7
      const start = new Date(this.viewYear, this.viewMonth, 1 - offset)
      const out = []
      for (let i = 0; i < 42; i++) {
        const d = new Date(start)
        d.setDate(start.getDate() + i)
        const y = d.getFullYear()
        const m = d.getMonth()
        const day = d.getDate()
        const iso = isoOf(y, m, day)
        const inMonth = m === this.viewMonth
        const isSelected = iso === this.selectedIso
        const isPastDisabled = this.disablePast && iso < this.todayIso && !isSelected
        out.push({
          key: iso,
          y,
          m,
          d: day,
          iso,
          inMonth,
          isSelected,
          isPastDisabled,
          isToday: iso === this.todayIso,
          hasAppointment: this.markedDates.includes(iso),
          cy: isSelected
            ? `${this.cyRoot}-selected`
            : `${this.cyRoot}-day-${iso}`
        })
      }
      return out
    }
  },
  watch: {
    value (newVal) {
      const parsed = this._parseValue(newVal)
      if (!parsed) return
      this.viewYear = parsed.getFullYear()
      this.viewMonth = parsed.getMonth()
    }
  },
  methods: {
    _parseValue (v) {
      const m = ISO_RE.exec(v || '')
      if (!m) return null
      return new Date(+m[1], +m[2] - 1, +m[3])
    },
    prevMonth () {
      if (this.disabled) return
      if (this.viewMonth === 0) {
        this.viewMonth = 11
        this.viewYear -= 1
      } else {
        this.viewMonth -= 1
      }
    },
    nextMonth () {
      if (this.disabled) return
      if (this.viewMonth === 11) {
        this.viewMonth = 0
        this.viewYear += 1
      } else {
        this.viewMonth += 1
      }
    },
    selectCell (cell) {
      if (this.disabled || cell.isPastDisabled) return
      if (!cell.inMonth) {
        this.viewYear = cell.y
        this.viewMonth = cell.m
      }
      this.$emit('input', cell.iso)
    }
  }
}
</script>

<style lang="less">
@import "../../styles/var";

@ios-cal-bg: #FFFFFF;
@ios-cal-text: #1C1C1E;
@ios-cal-muted: rgba(60, 60, 67, 0.35);
@ios-cal-label: rgba(60, 60, 67, 0.6);
@ios-cal-blue: #007AFF;
@ios-cal-blue-fade: rgba(0, 122, 255, 0.12);
@ios-cal-red: #FF3B30;

.ios-calendar {
  width: 100%;
  padding: 12px 12px 8px;
  background: @ios-cal-bg;
  border-radius: 14px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05), 0 4px 12px rgba(0, 0, 0, 0.04);
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  color: @ios-cal-text;
  box-sizing: border-box;
  user-select: none;

  &--disabled {
    opacity: 0.55;
    pointer-events: none;
  }

  .ios-calendar-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 4px 6px 10px;
  }

  .ios-calendar-title {
    flex: 1 1 auto;
    text-align: center;
    font-size: 1.7rem;
    font-weight: 600;
    letter-spacing: -0.2px;
  }

  .ios-calendar-nav {
    flex: 0 0 auto;
    width: 32px;
    height: 32px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: @ios-cal-blue;
    background: transparent;
    border: none;
    border-radius: 50%;
    cursor: pointer;
    transition: background-color 0.15s ease, transform 0.1s ease;

    &:hover:not(:disabled) {
      background-color: @ios-cal-blue-fade;
    }

    &:active:not(:disabled) {
      transform: scale(0.92);
    }

    &:disabled {
      opacity: 0.35;
      cursor: not-allowed;
    }
  }

  .ios-calendar-weekdays {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    padding: 0 2px 6px;
    font-size: 1.2rem;
    font-weight: 500;
    letter-spacing: 0.3px;
    text-align: center;
    color: @ios-cal-label;
    text-transform: uppercase;
  }

  .ios-calendar-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 2px;
  }

  .ios-calendar-day {
    aspect-ratio: 1 / 1;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1.6rem;
    font-weight: 400;
    color: @ios-cal-text;
    background: transparent;
    border: none;
    border-radius: 999px;
    cursor: pointer;
    font-family: inherit;
    transition: background-color 0.15s ease, color 0.15s ease, transform 0.08s ease;

    span {
      pointer-events: none;
    }

    &:hover:not(:disabled):not(.ios-calendar-day--selected) {
      background-color: @ios-cal-blue-fade;
    }

    &:active:not(:disabled) {
      transform: scale(0.92);
    }

    &--muted {
      color: @ios-cal-muted;
    }

    &--past {
      color: @ios-cal-muted;
      opacity: 0.4;
      cursor: not-allowed;
    }

    &--today {
      color: @ios-cal-red;
      font-weight: 600;
    }

    &--marked {
      color: @ios-cal-blue;
      font-weight: 600;
    }

    &--selected {
      background-color: @ios-cal-blue;
      color: white;
      font-weight: 600;
    }
  }

  @media only screen and (max-width: 720px) {
    padding: 10px 10px 6px;

    .ios-calendar-title {
      font-size: 1.6rem;
    }

    .ios-calendar-day {
      font-size: 1.55rem;
    }
  }
}
</style>
